import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CropperCanvas,
  CropperImage,
  CropperSelection,
  CropperGrid,
  CropperShade,
  CropperHandle,
} from 'cropperjs';

CropperCanvas.$define();
CropperImage.$define();
CropperSelection.$define();
CropperGrid.$define();
CropperShade.$define();
CropperHandle.$define();

/**
 * Composant Angular permettant d’afficher, transformer, recadrer
 * et exporter une image grâce à CropperJS v2 (WebComponents).
 */
@Component({
  selector: 'lib-cropperjs-picture-editor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cropperjs-picture-editor.component.html',
  styleUrl: './cropperjs-picture-editor.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CropperjsPictureEditorComponent implements AfterViewInit {
  @ViewChild('image') imageRef!: ElementRef<CropperImage>;
  @ViewChild('selection') selectionRef!: ElementRef<CropperSelection>;
  @ViewChild('canvas') canvasRef!: ElementRef<CropperCanvas>;

  public imgUrl!: string;

  @Input() modalTitle = 'Photo Editor';
  @Input() hideModalHeader = false;
  @Input() modalMaxWidth = '500px';
  @Input() aspectRatio = NaN;
  @Input() autoCropArea = 0.8;
  @Input() resizeToWidth!: number;
  @Input() resizeToHeight!: number;
  @Input() imageSmoothingEnabled = true;
  @Input() imageSmoothingQuality: ImageSmoothingQuality = 'high';
  @Input() format!: string;

  @Input() closeBtnText = 'Close';
  @Input() applyBtnText = 'Apply';

  quality = 92;

  @Input() set imageQuality(value: number) {
    if (value > 0 && value <= 100) {
      this.quality = value;
    } else {
      this.errorEvent.error('Invalid image quality');
    }
  }

  /* --- EVENTS --- */
  @Output() closeEvent = new EventEmitter<any>();
  @Output() imageCroppedEvent = new EventEmitter<NgxCroppedEvent>();
  @Output() errorEvent = new EventEmitter<any>();

  imageLoaded = false;
  isProcessing = false;
  private initialMatrix: number[] = [];

  /**
   * Setter appelé quand une nouvelle source d’image est fournie.
   * Gère automatiquement :
   *  - un File (upload)
   *  - un Event provenant d’un <input type="file">
   *  - une URL data:image/base64
   *  - une URL distante .jpg / .png …
   */
  @Input() set source(data: File | string | any) {
    // INPUT EVENT : <input type="file">
    if (data instanceof Event) {
      const file = (<HTMLInputElement>data.target).files?.[0];
      if (!file) return;

      if (!this.format) this.format = file.type.split('/')[1];

      const reader = new FileReader();
      reader.onload = (ev) => {
        this.imgUrl = ev.target!.result as string;
        this.onLoad();
      };
      reader.readAsDataURL(file);
      return;
    }

    // FICHIER DIRECT
    if (data instanceof File) {
      if (!this.format) this.format = data.type.split('/')[1];

      const reader = new FileReader();
      reader.onload = (ev) => {
        this.imgUrl = ev.target!.result as string;
        this.onLoad();
      };
      reader.readAsDataURL(data);
      return;
    }

    // BASE64
    if (typeof data === 'string' && data.startsWith('data:image')) {
      this.imgUrl = data;
      this.onLoad();
      return;
    }

    // URL
    if (typeof data === 'string' && this.isValidImageURL(data)) {
      this.imgUrl = data;
      this.onLoad();
      return;
    }

    this.errorEvent.emit('Not supported INPUT');
  }

  /**
   * Hook Angular exécuté après que la vue soit créée.
   * Configure uniquement des propriétés statiques (aspectRatio, initialCoverage).
   */
  ngAfterViewInit(): void {
    const image = this.imageRef?.nativeElement;
    const selection = this.selectionRef?.nativeElement;
    if (!image || !selection) return;
    if (this.aspectRatio) {
      selection.aspectRatio = this.aspectRatio;
    }
    if (this.autoCropArea) {
      selection.initialCoverage = this.autoCropArea;
    }
  }

  /**
   * Vérifie qu’une URL est bien une image.
   */
  isValidImageURL(str: string) {
    return str.match(/^http[^?]*\.(jpg|jpeg|gif|png|tiff|bmp)/gim);
  }

  /** Tourne l’image de +45° */
  rotateRight() {
    this.imageRef?.nativeElement?.$rotate(Math.PI / 4);
  }

  /** Tourne l’image de -45° */
  rotateLeft() {
    this.imageRef?.nativeElement?.$rotate(-Math.PI / 4);
  }

  /** Zoom AVANT (+10%) */
  zoomIn() {
    this.imageRef?.nativeElement?.$zoom(0.1);
  }

  /** Zoom ARRIÈRE (-10%) */
  zoomOut() {
    this.imageRef?.nativeElement?.$zoom(-0.1);
  }

  /** Retourne horizontalement (effet miroir) */
  flipH() {
    this.imageRef?.nativeElement?.$scale(-1, 1);
  }

  /** Retourne verticalement (haut ↔ bas) */
  flipV() {
    this.imageRef?.nativeElement?.$scale(1, -1);
  }

  /** Réinitialise complètement toutes les transformations appliquées à l’image */
  reset() {
    if (!this.imageRef?.nativeElement || !this.initialMatrix.length) return;

    const image = this.imageRef.nativeElement;
    image.$setTransform(this.initialMatrix);
  }

  /**
   * Exporte la zone sélectionnée :
   *  - crée un canvas contenant le crop
   *  - génère un Base64
   *  - génère un File (Blob)
   *  - émet un événement contenant les deux formats
   */
  async export() {
    this.isProcessing = true;
    this.imageLoaded = false;

    const selection = this.selectionRef?.nativeElement;
    if (!selection) {
      this.errorEvent.emit('No selection found');
      this.isProcessing = false;
      return;
    }

    const croppedCanvas = await selection.$toCanvas({
      width: this.resizeToWidth,
      height: this.resizeToHeight,
      beforeDraw: (ctx) => {
        ctx.imageSmoothingEnabled = this.imageSmoothingEnabled;
        ctx.imageSmoothingQuality = this.imageSmoothingQuality;
      },
    });

    const outputImage = croppedCanvas.toDataURL(
      'image/' + this.format,
      this.quality / 100,
    );

    croppedCanvas.toBlob(
      (blob) => {
        if (blob) {
          this.imageCroppedEvent.emit({
            base64: outputImage,
            file: new File([blob], Date.now() + '.' + this.format, {
              type: 'image/' + this.format,
            }),
          });
        }
        this.isProcessing = false;
        this.imageLoaded = true;
      },
      'image/' + this.format,
      this.quality / 100,
    );
  }

  /** Ferme le composant */
  close() {
    this.closeEvent.emit();
  }

  /**
   * Appelé dès que l’image source est chargée.
   * 1) Injecte dynamiquement l’URL dans le WebComponent (src)
   * 2) Attend que CropperJS ait terminé son layout ($ready)
   * 3) Récupère la position réelle de l’image
   * 4) Positionne automatiquement la sélection
   */
  async onLoad() {
    this.imageLoaded = true;

    const image = this.imageRef?.nativeElement;
    const selection = this.selectionRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;

    if (!image || !selection || !canvas || !this.imgUrl) return;

    image.src = this.imgUrl; // injection obligatoire pour WebComponent

    await image.$ready(); // attend que l’image soit affichée dans CropperJS

    // attente de 2 frames pour que les transformation de cropper sur l'image aient le temps d'être réalisées avant de prendre les dimensiosn
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    this.initialMatrix = image.$getTransform();

    const bbox = image.getBoundingClientRect();
    const cbbox = canvas.getBoundingClientRect();

    const x = bbox.left - cbbox.left;

    // on met la selection par rapport à la largeur de l'image si elle est cohérente en ne dépassant pas celle du canvas sinon on se base sur canvas
    Object.assign(selection, {
      x: bbox.width < cbbox.width ? x : 10,
      y: 20,
      width: bbox.width < cbbox.width ? bbox.width : cbbox.width - 20,
      height: 0, // hauteur auto-calculée par CropperJS
    });

    selection.$render();
  }

  /** Hook de nettoyage */
  ngOnDestroy(): void {}

  /** Envoie un événement d’erreur générique */
  error() {
    this.errorEvent.emit('Error loading image');
  }
}
export interface NgxCroppedEvent {
  base64?: string;
  file?: File;
}

export type imageFormat = 'gif' | 'jpeg' | 'tiff' | 'png' | 'webp' | 'bmp';
